import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../config/firebase";
import { useAuth } from "../../../config/AuthContext";
import CcpCpBreadcrumbs from "../../Layout/inc/CcpCpBreadcrumbs";

// CcpBetaTesting: one doc per school, doc ID === that school's doc ID in
// CcpSchools. Each doc holds a `features` map so this same collection can
// gate other beta features later without changing the schema:
//   { schoolId, schoolName, features: { coursePlanning: true, ... }, updatedAt, updatedBy }
//
// CcpDistrictBetaTesting mirrors that shape one level up: one doc per
// district, doc ID === the district name as it appears on district users'
// `district` field (users/{uid}.district — the same value the district
// dashboard uses to look up a district's schools via /DistrictSchools).
// Enabling a district here grants every user in that district access
// regardless of whether their individual school is also enabled — see
// useCoursePlanningBetaAccess, which checks both and ORs the result.
//   { district, features: { coursePlanning: true, ... }, updatedAt, updatedBy }
const FEATURE_KEY = "coursePlanning";

const CcpAdminCoursePlanningBetaTesting = () => {
  const { currentUser } = useAuth();

  const [schools, setSchools] = useState([]);
  const [enabledMap, setEnabledMap] = useState({});
  const [checkedMap, setCheckedMap] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [districts, setDistricts] = useState([]);
  const [districtEnabledMap, setDistrictEnabledMap] = useState({});
  const [districtCheckedMap, setDistrictCheckedMap] = useState({});
  const [savingDistricts, setSavingDistricts] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [schoolsSnap, betaSnap, districtUsersSnap, districtBetaSnap] =
          await Promise.all([
            getDocs(collection(db, "CcpSchools")),
            getDocs(collection(db, "CcpBetaTesting")),
            getDocs(
              query(
                collection(db, "users"),
                where("ccpDistrictFlag", "==", true),
              ),
            ),
            getDocs(collection(db, "CcpDistrictBetaTesting")),
          ]);

        const schoolList = schoolsSnap.docs
          .map((d) => ({
            id: d.id,
            school_name: d.data().school_name || "(Unnamed School)",
          }))
          .sort((a, b) => a.school_name.localeCompare(b.school_name));

        const enabled = {};
        betaSnap.docs.forEach((d) => {
          enabled[d.id] = !!d.data()?.features?.[FEATURE_KEY];
        });

        // Districts aren't a first-class collection — derive the list of
        // known district names from district-flagged users, same source of
        // truth useCoursePlanningBetaAccess reads at check time.
        const districtNames = Array.from(
          new Set(
            districtUsersSnap.docs
              .map((d) => d.data()?.district)
              .filter(Boolean),
          ),
        ).sort((a, b) => a.localeCompare(b));

        const districtEnabled = {};
        districtBetaSnap.docs.forEach((d) => {
          districtEnabled[d.id] = !!d.data()?.features?.[FEATURE_KEY];
        });

        setSchools(schoolList);
        setEnabledMap(enabled);
        setCheckedMap(enabled);

        setDistricts(districtNames);
        setDistrictEnabledMap(districtEnabled);
        setDistrictCheckedMap(districtEnabled);
      } catch (err) {
        console.error("Error loading beta testing data:", err);
        setSnackbar({
          open: true,
          message: "Error loading schools. Please try again.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Left panel: every school, filtered by search — used to turn access ON.
  const filteredSchools = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return schools;
    return schools.filter((s) => s.school_name.toLowerCase().includes(term));
  }, [schools, search]);

  // Right panel: only schools currently saved as enabled — used to review /
  // turn access OFF. Membership reflects the last-saved state (enabledMap),
  // not pending checkbox changes, so a school doesn't "jump" into this list
  // until Save actually confirms it's enabled.
  const currentlyEnabledSchools = useMemo(
    () => schools.filter((s) => enabledMap[s.id]),
    [schools, enabledMap],
  );

  const changedCount = useMemo(
    () =>
      schools.filter((s) => !!enabledMap[s.id] !== !!checkedMap[s.id]).length,
    [schools, enabledMap, checkedMap],
  );

  const allFilteredChecked =
    filteredSchools.length > 0 &&
    filteredSchools.every((s) => checkedMap[s.id]);

  const handleToggleSchool = (schoolId) => (e) => {
    const { checked } = e.target;
    setCheckedMap((prev) => ({ ...prev, [schoolId]: checked }));
  };

  const handleSelectAllFiltered = (e) => {
    const { checked } = e.target;
    setCheckedMap((prev) => {
      const next = { ...prev };
      filteredSchools.forEach((s) => {
        next[s.id] = checked;
      });
      return next;
    });
  };

  const handleUncheckAllEnabled = () => {
    setCheckedMap((prev) => {
      const next = { ...prev };
      currentlyEnabledSchools.forEach((s) => {
        next[s.id] = false;
      });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const changedSchools = schools.filter(
        (s) => !!enabledMap[s.id] !== !!checkedMap[s.id],
      );

      if (changedSchools.length === 0) {
        setSnackbar({
          open: true,
          message: "No changes to save.",
          severity: "info",
        });
        setSaving(false);
        return;
      }

      const batch = writeBatch(db);
      changedSchools.forEach((school) => {
        const ref = doc(db, "CcpBetaTesting", school.id);
        batch.set(
          ref,
          {
            schoolId: school.id,
            schoolName: school.school_name,
            features: { [FEATURE_KEY]: !!checkedMap[school.id] },
            updatedAt: Timestamp.now(),
            updatedBy: currentUser?.email || null,
          },
          { merge: true },
        );
      });

      await batch.commit();
      setEnabledMap(checkedMap);
      setSnackbar({
        open: true,
        message: `Saved ${changedSchools.length} school${
          changedSchools.length === 1 ? "" : "s"
        }.`,
        severity: "success",
      });
    } catch (err) {
      console.error("Error saving beta testing changes:", err);
      setSnackbar({
        open: true,
        message: "Error saving changes. Please try again.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const districtChangedCount = useMemo(
    () =>
      districts.filter(
        (d) => !!districtEnabledMap[d] !== !!districtCheckedMap[d],
      ).length,
    [districts, districtEnabledMap, districtCheckedMap],
  );

  const handleToggleDistrict = (district) => (e) => {
    const { checked } = e.target;
    setDistrictCheckedMap((prev) => ({ ...prev, [district]: checked }));
  };

  const handleSaveDistricts = async () => {
    setSavingDistricts(true);
    try {
      const changedDistricts = districts.filter(
        (d) => !!districtEnabledMap[d] !== !!districtCheckedMap[d],
      );

      if (changedDistricts.length === 0) {
        setSnackbar({
          open: true,
          message: "No changes to save.",
          severity: "info",
        });
        setSavingDistricts(false);
        return;
      }

      const batch = writeBatch(db);
      changedDistricts.forEach((district) => {
        const ref = doc(db, "CcpDistrictBetaTesting", district);
        batch.set(
          ref,
          {
            district,
            features: { [FEATURE_KEY]: !!districtCheckedMap[district] },
            updatedAt: Timestamp.now(),
            updatedBy: currentUser?.email || null,
          },
          { merge: true },
        );
      });

      await batch.commit();
      setDistrictEnabledMap(districtCheckedMap);
      setSnackbar({
        open: true,
        message: `Saved ${changedDistricts.length} district${
          changedDistricts.length === 1 ? "" : "s"
        }.`,
        severity: "success",
      });
    } catch (err) {
      console.error("Error saving district beta testing changes:", err);
      setSnackbar({
        open: true,
        message: "Error saving changes. Please try again.",
        severity: "error",
      });
    } finally {
      setSavingDistricts(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ paddingTop: "50px", pb: 6 }}>
      <CcpCpBreadcrumbs pageName="Beta Testing" />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 1,
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
          Course Planning — Beta Testing
        </Typography>
        {changedCount > 0 && (
          <Chip
            label={`${changedCount} unsaved change${changedCount === 1 ? "" : "s"}`}
            color="warning"
            size="small"
          />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Use the left list to grant Course Planning access to a school.
        Currently-enabled schools are shown on the right — uncheck a school
        there to remove its access. Click Save on either side to apply all
        pending changes.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Left: all schools — grant access */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2}>
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
                  All Schools
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || changedCount === 0}
                  sx={{ backgroundColor: "#a41e36" }}
                >
                  {saving ? <CircularProgress size={20} /> : "Save"}
                </Button>
              </Box>

              <Box sx={{ px: 2, pb: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Search schools"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Box>

              <Divider />

              <List sx={{ maxHeight: "60vh", overflowY: "auto" }}>
                <ListItem
                  dense
                  onClick={() =>
                    handleSelectAllFiltered({
                      target: { checked: !allFilteredChecked },
                    })
                  }
                  sx={{ cursor: "pointer", backgroundColor: "grey.50" }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={allFilteredChecked}
                      indeterminate={
                        !allFilteredChecked &&
                        filteredSchools.some((s) => checkedMap[s.id])
                      }
                      onChange={handleSelectAllFiltered}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography fontWeight="bold">
                        Select All ({filteredSchools.length})
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider />

                {filteredSchools.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No schools found." />
                  </ListItem>
                ) : (
                  filteredSchools.map((school) => (
                    <ListItem
                      key={school.id}
                      dense
                      onClick={() =>
                        handleToggleSchool(school.id)({
                          target: { checked: !checkedMap[school.id] },
                        })
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={!!checkedMap[school.id]}
                          onChange={handleToggleSchool(school.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </ListItemIcon>
                      <ListItemText primary={school.school_name} />
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>
          </Grid>

          {/* Right: currently-enabled schools — review / revoke access */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2}>
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
                  Currently Enabled ({currentlyEnabledSchools.length})
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || changedCount === 0}
                  sx={{ backgroundColor: "#a41e36" }}
                >
                  {saving ? <CircularProgress size={20} /> : "Save"}
                </Button>
              </Box>

              <Box sx={{ px: 2, pb: 2 }}>
                <Button
                  size="small"
                  onClick={handleUncheckAllEnabled}
                  disabled={currentlyEnabledSchools.length === 0}
                >
                  Uncheck All
                </Button>
              </Box>

              <Divider />

              <List sx={{ maxHeight: "60vh", overflowY: "auto" }}>
                {currentlyEnabledSchools.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No schools are currently enabled." />
                  </ListItem>
                ) : (
                  currentlyEnabledSchools.map((school) => (
                    <ListItem
                      key={school.id}
                      dense
                      onClick={() =>
                        handleToggleSchool(school.id)({
                          target: { checked: !checkedMap[school.id] },
                        })
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={!!checkedMap[school.id]}
                          onChange={handleToggleSchool(school.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </ListItemIcon>
                      <ListItemText primary={school.school_name} />
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>
          </Grid>

          {/* Districts: enabling a district grants Course Planning access to
              every user in that district, regardless of per-school access. */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ mt: 2 }}>
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
                  Districts
                </Typography>
                {districtChangedCount > 0 && (
                  <Chip
                    label={`${districtChangedCount} unsaved change${
                      districtChangedCount === 1 ? "" : "s"
                    }`}
                    color="warning"
                    size="small"
                  />
                )}
                <Button
                  variant="contained"
                  onClick={handleSaveDistricts}
                  disabled={savingDistricts || districtChangedCount === 0}
                  sx={{ backgroundColor: "#a41e36" }}
                >
                  {savingDistricts ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Save"
                  )}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
                Grants Course Planning access to every school in the district
                — useful for district users who don't have a single school of
                their own. Districts are listed from existing district-level
                user accounts.
              </Typography>

              <Divider />

              <List sx={{ maxHeight: "40vh", overflowY: "auto" }}>
                {districts.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No districts found." />
                  </ListItem>
                ) : (
                  districts.map((district) => (
                    <ListItem
                      key={district}
                      dense
                      onClick={() =>
                        handleToggleDistrict(district)({
                          target: { checked: !districtCheckedMap[district] },
                        })
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={!!districtCheckedMap[district]}
                          onChange={handleToggleDistrict(district)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </ListItemIcon>
                      <ListItemText primary={district} />
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CcpAdminCoursePlanningBetaTesting;
